import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { HeartIcon as Heart } from "@phosphor-icons/react";

function YesNo({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5" data-testid={testId}>
      {[["Yes", true], ["No", false]].map(([label, v]) => (
        <button key={label} type="button" onClick={() => onChange(value === v ? null : v)}
          className={`rounded-full px-3.5 py-1.5 text-[12px] font-medium border transition-colors ${
            value === v ? "bg-ink text-white border-ink" : "bg-white text-[#3a3a3a] border-[#E5E5E5] hover:border-ink"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

const LABELS = {
  meds_ok: "Comfortable administering medications?",
  puppies_ok: "Take puppies (under 1 year)?",
  seniors_ok: "Take senior dogs (8+ years)?",
  reactive_ok: "Comfortable with reactive dogs?",
  aggression_ok: "Take aggression / bite-history cases?",
  matted_coats_ok: "Take matted coats?",
  bite_history_ok: "Take dogs with a bite history?",
  giant_breeds_ok: "Take giant breeds (80+ lb)?",
  in_heat_ok: "Dogs in heat welcome?",
};

// Which questions each provider type answers — kept relevant so a
// video trainer is never asked "max dogs at a time".
const KIND_PREFS = {
  walker: ["meds_ok", "puppies_ok", "seniors_ok", "reactive_ok"],
  sitter: ["meds_ok", "puppies_ok", "seniors_ok", "reactive_ok"],
  trainer: ["reactive_ok", "aggression_ok", "puppies_ok", "seniors_ok"],
  groomer: ["matted_coats_ok", "bite_history_ok", "giant_breeds_ok", "puppies_ok", "seniors_ok", "reactive_ok"],
  field_host: ["reactive_ok", "in_heat_ok"],
  other: ["puppies_ok", "seniors_ok", "reactive_ok"],
};

const KIND_LABEL_OVERRIDES = {
  field_host: { reactive_ok: "Reactive dogs welcome? (a private field is their safe space)" },
};

// field_host max-dogs lives on the land profile card, not here
const MAX_DOGS_LABEL = { walker: "Max dogs at a time", sitter: "Max dogs at a time" };

const KIND_BLURBS = {
  trainer: "Owners see a match score against their dogs' Care Passports — including the new Training & behavior section. Answering these puts you in front of the right clients.",
  groomer: "Owners see a match score against their dogs' Care Passports (coat, sensitivities, bite history). Answering these puts you in front of the right clients.",
  field_host: "These show as badges on your listing — welcoming reactive dogs and dogs in heat is a big reason owners book private fields.",
};

/** Provider's care preferences — kind-aware; powers the customer-facing match score. */
export default function ProviderCarePrefs({ provider, onRefresh }) {
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => setPrefs(provider?.care_prefs || {}), [provider]);
  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const kind = provider?.kind || "other";
  const keys = KIND_PREFS[kind] || KIND_PREFS.other;
  const overrides = KIND_LABEL_OVERRIDES[kind] || {};
  const maxDogsLabel = MAX_DOGS_LABEL[kind];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/providers/mine", { care_prefs: prefs });
      toast.success("Care preferences saved — you'll now show match scores to the right owners");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 mt-6" data-testid="provider-care-prefs">
      <h2 className="font-spectral text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Heart size={20} weight="duotone" className="text-crimson" /> Care preferences
      </h2>
      <p className="text-[12.5px] text-[#6B6B6B] mb-4 leading-relaxed">
        {KIND_BLURBS[kind] || (
          <>Owners see a <strong>match score</strong> against their dogs' Care Passports — answering these puts you in front of the right clients.</>
        )}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {keys.map((k) => (
          <div key={k}>
            <Label className="text-[11.5px] text-[#3a3a3a] mb-1.5 block">{overrides[k] || LABELS[k]}</Label>
            <YesNo testId={`pref-${k}`} value={prefs[k] ?? null} onChange={(v) => set(k, v)} />
          </div>
        ))}
        {maxDogsLabel && (
          <div>
            <Label className="text-[11.5px] text-[#3a3a3a] mb-1.5 block">{maxDogsLabel}</Label>
            <Input type="number" min="1" max="20" className="w-24" value={prefs.max_dogs ?? ""}
              onChange={(e) => set("max_dogs", e.target.value ? parseInt(e.target.value, 10) : null)}
              data-testid="pref-max-dogs" />
          </div>
        )}
      </div>
      <Button onClick={save} disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white mt-4" data-testid="pref-save">
        {busy ? "Saving…" : "Save preferences"}
      </Button>
    </div>
  );
}
